import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';
import GameCard from './GameCard';

const SORT_OPTIONS = [
{ value: 'newest', label: 'Newest First' },
{ value: 'oldest', label: 'Oldest First' },
{ value: 'popular', label: 'Most Viewed' },
{ value: 'name', label: 'Name A-Z' }];


const FILTER_MODES = [
{ value: 'all', label: 'All' },
{ value: 'text-to-game', label: 'Games' },
{ value: 'prompt-to-mini-game', label: 'Mini Games' },
{ value: 'text-to-animation', label: 'Animations' },
{ value: 'script-to-video', label: 'Videos' },
{ value: 'music-to-video', label: 'Music' },
{ value: 'text-to-music', label: 'Music Gen' },
{ value: 'lip-sync', label: 'Lip Sync' },
{ value: 'image-to-video', label: 'Img to Video' }];


const MONKEY_RIDE_GAME = {
  id: 'monkey-ride-3d',
  title: 'Monkey Ride 3D',
  type: 'game',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_195917fcc-1773040900381.png",
  thumbnailAlt: 'Colorful 3D endless runner game with a monkey character riding through vibrant environments',
  genre: 'Endless Runner',
  difficulty: 'Medium',
  plays: 0,
  rating: 5.0,
  creator: 'LoopNest AI',
  createdAt: new Date()?.toLocaleDateString(),
  engine: 'Babylon.js',
  prompt: 'Monkey Ride 3D - A full-featured endless runner with 5 worlds, characters, vehicles, power-ups, missions, and leaderboard',
  isMonkeyRide: true
};

export default function GamesLibrary({ games, onPlay, onEdit, onDuplicate, onPublish, onTogglePublish, onShare, onDelete, onRemix, onTrailer, onPublishPlatform, onMonetize, onEditVideo }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [filterMode, setFilterMode] = useState('all');
  const [showPublishedOnly, setShowPublishedOnly] = useState(false);

  const filtered = useMemo(() => {
    let list = [...games];
    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter((g) => g?.title?.toLowerCase()?.includes(q) || g?.prompt?.toLowerCase()?.includes(q));
    }
    if (filterMode !== 'all') {
      list = list?.filter((g) => g?.mode === filterMode);
    }
    if (showPublishedOnly) {
      list = list?.filter((g) => g?.published);
    }
    if (sort === 'newest') list?.sort((a, b) => b?.id - a?.id);else
    if (sort === 'oldest') list?.sort((a, b) => a?.id - b?.id);else
    if (sort === 'popular') list?.sort((a, b) => (b?.views || 0) - (a?.views || 0));else
    if (sort === 'name') list?.sort((a, b) => a?.title?.localeCompare(b?.title));
    return list;
  }, [games, search, sort, filterMode, showPublishedOnly]);

  // Step 2: Download game as ZIP
  const handleDownloadGame = (game) => {
    const filename = `${game?.title?.replace(/\s+/g, '_') || 'game'}.zip`;
    if (game?.gameUrl || game?.outputUrl) {
      const a = document.createElement('a');
      a.href = game?.gameUrl || game?.outputUrl;
      a.download = filename;
      a?.click();
    } else {
      // Show info toast — game package not yet available for download
      alert(`"${game?.title}" — ZIP download ready when game package is fully generated`);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Monkey Ride 3D Featured Banner */}
      <div
        className="p-4 rounded-2xl flex items-center gap-4 cursor-pointer hover:scale-[1.01] transition-transform"
        style={{
          background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
          border: '2px solid rgba(99,102,241,0.4)'
        }}
        onClick={() => window.location.href = '/monkey-ride'}>
        
        <div className="text-4xl">🐒</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-heading font-bold text-sm" style={{ color: 'var(--color-foreground)' }}>Monkey Ride 3D</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(99,102,241,0.2)', color: 'var(--color-primary)' }}>NEW</span>
          </div>
          <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Full 3D endless runner • 5 worlds • Characters • Power-ups • Missions</p>
        </div>
        <div
          className="px-4 py-2 rounded-xl font-bold text-sm text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
          
          Play Now
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base md:text-lg font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            My Games
          </h2>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {filtered?.length} of {games?.length} creation{games?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Published filter toggle */}
          <button
            onClick={() => setShowPublishedOnly((v) => !v)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all"
            style={{
              borderColor: showPublishedOnly ? 'var(--color-success)' : 'var(--color-border)',
              background: showPublishedOnly ? 'rgba(16,185,129,0.1)' : 'var(--color-card)',
              color: showPublishedOnly ? 'var(--color-success)' : 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-caption)'
            }}>
            
            <Icon name="Globe" size={12} />
            Published
          </button>
          {/* Sort */}
          <select
            value={sort}
            onChange={(e) => setSort(e?.target?.value)}
            className="text-xs px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)', fontFamily: 'var(--font-caption)' }}
            aria-label="Sort games">
            
            {SORT_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
          </select>
        </div>
      </div>

      {/* Search */}
      <Input
        type="search"
        placeholder="Search games by title or prompt..."
        value={search}
        onChange={(e) => setSearch(e?.target?.value)}
        aria-label="Search games" />
      

      {/* Mode filter tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {FILTER_MODES?.map((f) =>
        <button
          key={f?.value}
          onClick={() => setFilterMode(f?.value)}
          className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full border transition-all"
          style={{
            borderColor: filterMode === f?.value ? 'var(--color-primary)' : 'var(--color-border)',
            background: filterMode === f?.value ? 'rgba(99,102,241,0.1)' : 'var(--color-card)',
            color: filterMode === f?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
            fontFamily: 'var(--font-caption)'
          }}>
          
            {f?.label}
          </button>
        )}
      </div>

      {/* Grid */}
      {filtered?.length === 0 ?
      <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
            <Icon name="Gamepad2" size={28} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>No games found</p>
          <p className="text-xs text-center" style={{ color: 'var(--color-muted-foreground)' }}>
            {search ? 'Try a different search term' : showPublishedOnly ? 'No published games yet' : 'Generate your first game above!'}
          </p>
        </div> :

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {filtered?.map((game) =>
        <GameCard
          key={game?.id}
          game={game}
          onPlay={onPlay}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onPublish={onPublish}
          onTogglePublish={onTogglePublish}
          onShare={onShare}
          onDelete={onDelete}
          onRemix={onRemix}
          onTrailer={onTrailer}
          onPublishPlatform={onPublishPlatform}
          onMonetize={onMonetize}
          onEditVideo={onEditVideo}
          onDownload={handleDownloadGame} />

        )}
        </div>
      }
    </div>);

}